import {
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DataContext from '../context';
import { getAddCourseAction } from '../actions/course';
import { enforceMatchingSelections } from '../utils/matchingUtils';
import { findCourseExclusionConflicts } from '../utils/courseExclusions';
import getSections from '../timetable/getSections';
import Dialog from './Dialog';
import Presentation from './Presentation';

import styles from './SearchBar.module.css';

const SearchBar = memo(() => {
    const { data, selectedTerm, setSelectedTerm } = useContext(DataContext);
    const timetable = useSelector(state => (state.app.timetable));
    const dispatch = useDispatch();
    const inputRef = useRef();
    const dropdownRef = useRef();
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [exclusionNotice, setExclusionNotice] = useState(null);
    const [skipExclusionChecks, setSkipExclusionChecks] = useState(false);
    const [skipFutureExclusionChecks, setSkipFutureExclusionChecks] = useState(false);

    // Get available terms for dropdown
    const availableTerms = useMemo(() => {
        if (!data || !data.terms) return [];

        const terms = [];
        // Skip 'current' key, only get numbered keys
        Object.keys(data.terms).forEach(key => {
            if (key !== 'current') {
                terms.push(data.terms[key]);
            }
        });
        return terms;
    }, [data]);

    // Filtered search hints - exclude already added courses
    const allCourses = useMemo(() => {
        if (!data) {
            return [];
        }

        const ret = [];
        Object.entries(data).forEach(([courseCode, value]) => {
            if (courseCode === 'terms' || courseCode === 'lastUpdated') {
                return;
            }
            // Skip courses already in timetable
            if (timetable.hasOwnProperty(courseCode)) {
                return;
            }
            ret.push({
                code: courseCode,
                name: value.name,
                label: `${courseCode}: ${value.name}`
            });
        });
        ret.sort((a, b) => a.label.localeCompare(b.label));

        return ret;
    }, [data, timetable]);

    // Filter based on input
    const filteredCourses = useMemo(() => {
        if (!inputValue.trim()) return allCourses.slice(0, 8); // Show first 8 when empty

        const query = inputValue.toLowerCase();
        return allCourses
            .filter(course =>
                course.code.toLowerCase().includes(query) ||
                course.name.toLowerCase().includes(query)
            )
            .slice(0, 10); // Limit to 10 results
    }, [allCourses, inputValue]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                inputRef.current && !inputRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const commitCourse = useCallback((courseCode) => {
        if (data && data[courseCode]) {
            const sections = getSections(data, courseCode);
            const defaults = Object.values(sections || {}).map(values => values[0]).filter(Boolean);
            dispatch(getAddCourseAction(
                courseCode,
                enforceMatchingSelections(data[courseCode], defaults)
            ));
            setInputValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    }, [data, dispatch]);

    const handleAddCourse = useCallback((courseCode) => {
        if (!data?.[courseCode]) return;

        const conflicts = skipExclusionChecks
            ? []
            : findCourseExclusionConflicts(data, timetable, courseCode);
        if (conflicts.length > 0) {
            setIsOpen(false);
            setSkipFutureExclusionChecks(false);
            setExclusionNotice({ courseCode, conflicts });
            return;
        }

        commitCourse(courseCode);
    }, [commitCourse, data, skipExclusionChecks, timetable]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredCourses[highlightedIndex]) {
            handleAddCourse(filteredCourses[highlightedIndex].code);
        } else if (inputValue) {
            const courseCode = inputValue.split(':')[0].trim().toUpperCase();
            handleAddCourse(courseCode);
        }
    };

    const handleTermChange = (event) => {
        const newTerm = event.target.value;
        setSelectedTerm(newTerm || null);
    };

    const handleInputChange = (event) => {
        setInputValue(event.target.value);
        setIsOpen(true);
        setHighlightedIndex(-1);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredCourses.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Escape':
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            case 'Enter':
                if (highlightedIndex >= 0) {
                    e.preventDefault();
                    handleAddCourse(filteredCourses[highlightedIndex].code);
                }
                break;
            default:
                break;
        }
    };

    const handleOptionClick = (course) => {
        handleAddCourse(course.code);
    };

    return (
        <div className={styles.searchWrapper}>
            <form
                className={styles.searchBar}
                onSubmit={handleSubmit}
            >
                <div className={styles.termInfo}>
                    <select
                        className={styles.termSelect}
                        value={selectedTerm || ''}
                        onChange={handleTermChange}
                    >
                        {!selectedTerm && (
                            <option value="">Select a semester...</option>
                        )}
                        {/* Hardcoded common terms for initial selection */}
                        {!data && (
                            <>
                                <option value="2540">2025-26 Summer</option>
                                <option value="2530">2025-26 Spring</option>
                                <option value="2520">2024-25 Winter</option>
                                <option value="2510">2024-25 Fall</option>
                                <option value="2440">2023-24 Summer</option>
                                <option value="2430">2023-24 Spring</option>
                                <option value="2420">2023-24 Winter</option>
                                <option value="2410">2023-24 Fall</option>
                            </>
                        )}
                        {/* Once data is loaded, show actual terms from data */}
                        {data?.terms?.current && (
                            <option value={data.terms.current.num}>
                                {data.terms.current.text}
                            </option>
                        )}
                        {data && availableTerms.map((term) => (
                            term.num !== data?.terms?.current?.num && (
                                <option key={term.num} value={term.num}>
                                    {term.text}
                                </option>
                            )
                        ))}
                    </select>
                </div>
                <div className={styles.inputContainer}>
                    <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input
                        ref={inputRef}
                        className={styles.input}
                        id="add"
                        type="text"
                        placeholder="Search courses (e.g., COMP1021, calculus)..."
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        disabled={!data}
                        autoComplete="off"
                    />
                    {inputValue && (
                        <button
                            type="button"
                            className={styles.clearButton}
                            onClick={() => { setInputValue(''); setIsOpen(false); }}
                        >
                            ×
                        </button>
                    )}
                </div>
            </form>

            {/* Custom Dropdown */}
            {isOpen && filteredCourses.length > 0 && (
                <div ref={dropdownRef} className={styles.dropdown}>
                    {filteredCourses.map((course, index) => (
                        <div
                            key={course.code}
                            className={`${styles.dropdownItem} ${index === highlightedIndex ? styles.highlighted : ''}`}
                            onClick={() => handleOptionClick(course)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            <span className={styles.courseCode}>{course.code}</span>
                            <span className={styles.courseName}>{course.name}</span>
                        </div>
                    ))}
                </div>
            )}

            {isOpen && inputValue && filteredCourses.length === 0 && (
                <div ref={dropdownRef} className={styles.dropdown}>
                    <div className={styles.noResults}>
                        No courses found matching "{inputValue}"
                    </div>
                </div>
            )}
            {exclusionNotice && (
                <Presentation>
                    <Dialog
                        title="Course exclusion detected"
                        actions={[
                            {
                                label: 'Cancel',
                                onClick: () => {
                                    setSkipFutureExclusionChecks(false);
                                    setExclusionNotice(null);
                                },
                            },
                            {
                                label: 'Add anyway',
                                onClick: () => {
                                    if (skipFutureExclusionChecks) setSkipExclusionChecks(true);
                                    commitCourse(exclusionNotice.courseCode);
                                    setSkipFutureExclusionChecks(false);
                                    setExclusionNotice(null);
                                },
                            },
                        ]}
                    >
                        <p>
                            <strong>{exclusionNotice.courseCode}</strong> has an exclusion conflict with
                            {' '}{exclusionNotice.conflicts.join(', ')}.
                        </p>
                        <p>Students normally should not enroll in both courses.</p>
                        <label className={styles.skipExclusionOption}>
                            <input
                                type="checkbox"
                                checked={skipFutureExclusionChecks}
                                onChange={event => setSkipFutureExclusionChecks(event.target.checked)}
                            />
                            Skip future exclusion checks for this session
                        </label>
                    </Dialog>
                </Presentation>
            )}
        </div>
    );
});

export default SearchBar;
